import {
  Box,
  Button,
  classSet
} from '@platform/ui-library';
import {
  useEffect,
  useState
} from 'react';
import {
  Input,
  InputFieldType,
  LoadingSpinner,
  Select
} from '@app/form_components';
import { useServices } from '@app/hooks';
import {
  CheckOption,
  DesignIntent,
  Option
} from '@app/types';
import { OnChangeUtterance } from './UtteranceList';
import styles from './generate-utterances.module.scss';

export interface GenerateUtterancesProps {
  intent: DesignIntent;
  isOpen: boolean;
  onChangeUtterance: OnChangeUtterance;
  onClose: () => void;
}

export function GenerateUtterances({
  intent,
  isOpen,
  onChangeUtterance,
  onClose
}: GenerateUtterancesProps) {
  const {
    errorMessageService,
    intentService,
    loadingService
  } = useServices();
  const [context, setContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [languageModel, setLanguageModel] = useState('');
  const [languageModelOptions, setLanguageModelOptions] = useState<Option<string>[]>([]);
  const [utterances, setUtterances] = useState<CheckOption[]>([]);

  useEffect(() => {
    loadingService.useLoading(async () => {
      const newLanguageModelOptions = await intentService.getLanguageModelOptions();
      setLanguageModelOptions(newLanguageModelOptions);
      setLanguageModel(newLanguageModelOptions[0].value);
    });
  }, []);

  const onGenerateUtterances = async () => {
    setIsGenerating(true);
    try {
      const utterances = await intentService.generatedUtterances({
        intent,
        context,
        provider: languageModel
      });
      setUtterances(utterances.map((label) => {
        return {
          checked: false,
          label
        };
      }));
    } catch (error) {
      errorMessageService.addError(error as Error);
    }

    setIsGenerating(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box className="slide-in flex-column" theme="noPadding">
      <div className="box flex-column header">
        <h2 className={styles.title}>Generate Utterances</h2>
        <h3 className={styles.description}>Edit and approve generated utterances before adding them to your intent.</h3>
      </div>
      <div className="flex-gap flex-grow">
        <div className="box content">
          <Select
            label="LLM Provider"
            name="languageModel"
            onChange={(value) => setLanguageModel(value ?? '')}
            options={languageModelOptions}
            value={languageModel}
          />
          <Input
            label="LLM Context"
            name="llmContext"
            onChange={setContext}
            placeholder={window.l.intents.generate_utterances_context_placeholder}
            style={{ minHeight: '7em' }}
            type={InputFieldType.textarea}
            value={context}
          />
          <div className="flex-end">
            <Button
              type='button'
              theme='default'
              style={{ zIndex: 0 }}
              onClick={onGenerateUtterances}
            >
              Generate
            </Button>
          </div>
          <div className="flex-grow" />
        </div>
        <div className={classSet([styles.utteranceContainer, 'flex-grow'])}>
          {(isGenerating) ? (
            <LoadingSpinner
              className="flex-center flex-grow"
              message="Generating utterances..."
            />
          ) : (
            <div className="box content">
              {utterances.map(({ checked, label }, index) => {
                return (
                  <label className="clickable flex-gap" key={label}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setUtterances((utterances) => {
                          return utterances.map((
                            { checked, label },
                            mappedIndex
                          ) => {
                            return {
                              checked: (mappedIndex !== index) ? checked : !checked,
                              label
                            };
                          });
                        });
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="box footer">
        <Button
          type='button'
          theme="default"
          onClick={() => {
            if (!utterances.some((utterance) => utterance.checked)) {
              errorMessageService.addError('Please select utterances to add.');
              return;
            }

            utterances.filter(({ checked }) => {
              return checked;
            }).forEach(({ label }) => {
              onChangeUtterance({
                payload: { value: label },
                type: 'add'
              });
            });
            // Remove added utterances.
            setUtterances(utterances.filter(({ checked }) => {
              return !checked;
            }));
            onClose();
          }}
        >
          Add Selected Utterances
        </Button>
        <Button
          type='button'
          theme='outline'
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>
    </Box >
  );
}
